import React, { useState, useEffect } from 'react';
import Glitch from 'glitch-javascript-sdk';
import Navigate from '../../../../util/Navigate';
import { useNavigate } from 'react-router-dom';
import GameTitleForm from '../../component/section/titles/title_form';
import Loading from '../../component/alerts/Loading';
import timeouts from '../../../../constants/timeouts';
import Danger from '../../component/alerts/Danger';

const PublisherOnboardinStep4Page = () => {
    const [title, setTitle] = useState({ is_media_kit: false });
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [community, setCommunity] = useState({});
    const [gameMainImageBlob, setMainImageBlob] = useState(null);
    const [gameBannerImageBlob, setBannerImageBlob] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState('Searching for game data...');
    const [loadError, setLoadError] = useState(false);
    const navigate = useNavigate();
    const [externalGameTitle, setExternalGameTitle] = useState(null);
    const [communities, setCommunities] = useState([]);

    const [mediaIds, setMediaIds] = useState({
        pendingMediaIds: [],
        removedMediaIds: [],
    });

    useEffect(() => {
        Glitch.api.Communities.list({ roles: [Glitch.constants.Roles.ADMINISTRATOR, Glitch.constants.Roles.SUPER_ADMINISTRATOR, Glitch.constants.Roles.MODERATOR], order_by: 'name' }).then(response => {
            setCommunities(response.data.data)

            if (response.data.data && response.data.data.length === 1) {
                title.community_id = response.data.data[0].id;
            }

        }).catch(error => {
            console.error(error);
        });

    }, []);

    const handleMediaIdsUpdate = ({ pendingMediaIds, removedMediaIds }) => {
        setMediaIds({ pendingMediaIds, removedMediaIds });
    };

    const handleSubmit = async () => {
        setIsLoading(true);

        try {
            const titleData = { ...title, is_media_kit: false };

            if (!title.id) {
                const response = await Glitch.api.Titles.create(titleData);
                const createdTitle = response.data.data;

                const uploadPromises = [];

                if (gameMainImageBlob) {
                    uploadPromises.push(
                        Glitch.api.Titles.uploadMainImageBlob(createdTitle.id, gameMainImageBlob)
                    );
                }

                if (gameBannerImageBlob) {
                    uploadPromises.push(
                        Glitch.api.Titles.uploadBannerImageBlob(createdTitle.id, gameBannerImageBlob)
                    );
                }

                if (mediaIds.pendingMediaIds.length > 0) {
                    mediaIds.pendingMediaIds.forEach((mediaId, idx) => {
                        uploadPromises.push(
                            Glitch.api.Titles.addMedia(createdTitle.id, { media_id: mediaId, order: idx })
                        );
                    });
                }

                if (mediaIds.removedMediaIds.length > 0) {
                    mediaIds.removedMediaIds.forEach((mediaId) => {
                        uploadPromises.push(
                            Glitch.api.Titles.removeMedia(createdTitle.id, mediaId)
                        );
                    });
                }

                if (uploadPromises.length > 0) {
                    try {
                        await Promise.all(uploadPromises);
                    } catch (error) {
                        console.error(error);
                    }
                }

                navigate(Navigate.publishersOnboardingStep5Page(createdTitle.id));
            } else {
                await Glitch.api.Titles.update(title.id, titleData);

                const uploadPromises = [];

                if (gameMainImageBlob) {
                    uploadPromises.push(
                        Glitch.api.Titles.uploadMainImageBlob(title.id, gameMainImageBlob)
                    );
                }

                if (gameBannerImageBlob) {
                    uploadPromises.push(
                        Glitch.api.Titles.uploadBannerImageBlob(title.id, gameBannerImageBlob)
                    );
                }

                if (mediaIds.pendingMediaIds.length > 0) {
                    mediaIds.pendingMediaIds.forEach((mediaId, idx) => {
                        uploadPromises.push(
                            Glitch.api.Titles.addMedia(title.id, { media_id: mediaId, order: idx })
                        );
                    });
                }

                if (mediaIds.removedMediaIds.length > 0) {
                    mediaIds.removedMediaIds.forEach((mediaId) => {
                        uploadPromises.push(
                            Glitch.api.Titles.removeMedia(title.id, mediaId)
                        );
                    });
                }

                if (uploadPromises.length > 0) {
                    await Promise.all(uploadPromises);
                }

                navigate(Navigate.publishersOnboardingStep5Page());
            }
        } catch (error) {
            const jsonErrors = error?.response?.data;
            if (jsonErrors) {
                setErrors(jsonErrors);
                setTimeout(() => {
                    setErrors({});
                }, timeouts.error_message_timeout);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container-fluid px-3 py-4">
            {/* Hero Section */}
            <div className="text-center mb-5">
                <div className="badge bg-light text-dark mb-3 p-2">Step 4 of 4</div>
                <h1 className="display-5 fw-bold mb-3">Tell us About Your Game! 🎊</h1>
                <p className="lead mx-auto" style={{ maxWidth: '700px' }}>
                    Now tell us some basic information about your game so you can begin marketing it. You do not need to fill out everything yet and can always come back later to finish.
                </p>
            </div>


            <GameTitleForm
                gameTitle={title}
                campaignData={{}}
                externalGameData={externalGameTitle}
                communities={communities}
                setGameTitle={setTitle}
                setMainImageBlob={setMainImageBlob}
                setBannerImageBlob={setBannerImageBlob}
                errors={errors}
                showPressKit={false}
                isCreate={true}
                requireCommunity={true}
                onMediaIdsUpdate={handleMediaIdsUpdate}
            />

            <div className='container'>

                {Object.keys(errors).length > 0 &&
                    Object.keys(errors).map((errorKey) => {
                        const errorValue = errors[errorKey];

                        if (Array.isArray(errorValue)) {
                            return errorValue.map((message, index) => (
                                <Danger key={`${errorKey}-${index}`} message={message} />
                            ));
                        }

                        return (
                            <Danger key={errorKey} message={errorValue} />
                        );
                    })}

                {errors && Object.keys(errors).length > 0 && (
                    <Danger message="You have errors in your submission." />
                )}
            </div>

            <div className="mt-4 text-center">


                <button
                    type="button"
                    onClick={handleSubmit}
                    className="btn btn-success mx-2"
                    disabled={isLoading}
                >
                    {isLoading ? <Loading /> : 'Create Game'}
                </button>

            </div>



        </div>
    );
};

export default PublisherOnboardinStep4Page;